import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { FeatureFlagService } from 'src/app/modules/feature-flags/services/feature-flag/feature-flag.service';
import { KnowledgeBaseService } from 'src/app/modules/olp-api/services/kb/kb.service';
import { FontSize, MessageCodes } from '../../enums';
import { Settings } from '../../interfaces';
import { UserService } from '../user/user.service';
import { MessageService } from 'src/app/modules/message/services/message/message.service';
import { FeatureFlags } from '../../enums/feature-flags';

@Injectable({
  providedIn: 'root',
})
export class SettingsService implements OnDestroy {
  readonly KB_SECTION = 'HTMLActivityStorage';
  readonly KB_KEY = 'Settings';
  readonly STARTER_SETTINGS: Settings = {
    subtitlesOn: false,
    subtitlesFontSize: FontSize.MEDIUM,
    readAloud: false,
    prefersReducedMotion: false,
    muteSfx: false,
  };
  readonly MIN_READ_ALOUD_ENABLE_GRADE = 3;
  SAVE_TIMEOUT = 500;
  settings = new BehaviorSubject<Settings>({
    ...this.STARTER_SETTINGS,
  });
  sub!: Subscription;
  postTimeOut!: NodeJS.Timeout;

  constructor(
    private kbService: KnowledgeBaseService,
    private featureFlagService: FeatureFlagService,
    private userService: UserService,
    private messageService: MessageService
  ) {
    this.sub = new Subscription();
    this.sub.add(
      this.userService.grade$.subscribe(grade => {
        if (!this.settings.getValue().readAloud) {
          this.settings.next({
            ...this.settings.getValue(),
            readAloud: grade >= this.MIN_READ_ALOUD_ENABLE_GRADE,
          });
        }
      })
    );
  }

  // Reference: https://github.com/istation-hydra/olp-student-experience/blob/develop/projects/stage/src/app/app.module.ts
  async initSettings(): Promise<void> {
    if (this.featureFlagService.isFlagEnabled(FeatureFlags.USE_KB)) {
      console.log(`we're getting settings from kb!`);
      const kbSettings = (await this.kbService.get(
        this.KB_SECTION,
        this.KB_KEY
      )) as Settings;

      if (kbSettings) {
        // force some user settings for PreK2 users
        if (this.userService.grade$.getValue() < 3) {
          this.settings.next({
            ...this.settings.getValue(),
            ...kbSettings,
            prefersReducedMotion: false,
            muteSfx: false,
            readAloud: false,
          });
        } else {
          this.settings.next({ ...this.settings.getValue(), ...kbSettings });
        }
      }
    }

    console.log('INIT - Settings: ', this.settings.getValue());
  }

  saveSettings(settings?: Settings): void {
    if (settings) {
      this.settings.next({ ...this.settings.getValue(), ...settings });
    }

    // save settings
    if (this.featureFlagService.isFlagEnabled(FeatureFlags.USE_KB)) {
      // delay saving so can't spam toggle saving
      clearTimeout(this.postTimeOut);
      this.postTimeOut = setTimeout(() => {
        const currentSettings = this.settings.getValue();
        const userGrade = this.userService.grade$.getValue();

        // omit reducedMotion and muteSfx for PreK2 users
        const settingsToSave =
          userGrade < 3
            ? ((): Settings => {
                /* eslint-disable unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars */
                const { prefersReducedMotion, muteSfx, readAloud, ...rest } =
                  currentSettings;
                return rest as Settings;
                /* eslint-enable unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars */
              })()
            : currentSettings;

        this.kbService
          .setAndSync(this.KB_SECTION, this.KB_KEY, settingsToSave)
          .then(() => {
            console.log('SUCCESS - Saved Settings to kb:', settingsToSave);
          })
          .catch((error: Response) => {
            if (error.status === 409) {
              this.messageService.showMessage(
                MessageCodes.HTTP_UNEXPECTED_NET_ERROR
              );
            }
            console.log('ERROR - Failed to save kb settings:', settingsToSave);
          });
      }, this.SAVE_TIMEOUT);
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}
